import { ApiError } from '@/network/api-client';
import type { BiographyApi, BiographyClock, BiographyCommand, BiographySnapshot } from '../types/biography.types';
import { biographyRemaining } from '../utils/biography-clock';
export class BiographyController {
  private state: { snapshot: BiographySnapshot | null; clock: BiographyClock | null; loading: boolean; loadError: boolean; sending: boolean; error: boolean; submitted: boolean } = { snapshot: null, clock: null, loading: false, loadError: false, sending: false, error: false, submitted: false };
  private listeners = new Set<() => void>();
  private active = false;
  private generation = 0;
  private requests = new Set<AbortController>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private command: BiographyCommand | null = null;
  constructor(private readonly heroId: number | null, private readonly api: BiographyApi, private readonly newId: () => string = () => crypto.randomUUID()) {}
  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private update(patch: Partial<typeof this.state>) { if (!this.active) return; this.state = { ...this.state, ...patch }; this.listeners.forEach(listener => listener()); }
  start = () => { this.active = true; this.generation++; if (this.heroId !== null) void this.refresh(); };
  stop = () => { this.active = false; this.generation++; this.requests.forEach(request => request.abort()); this.requests.clear(); if (this.timer) clearTimeout(this.timer); this.timer = null; this.state = { ...this.state, sending: false }; };
  refresh = async () => {
    if (!this.active || this.heroId === null) return;
    const generation = this.generation, request = new AbortController(); this.requests.add(request);
    if (!this.state.snapshot) this.update({ loading: true, loadError: false });
    try {
      const snapshot = await this.api.snapshot(request.signal);
      if (!request.signal.aborted && generation === this.generation) {
        const changedCycle = this.state.snapshot?.cycle?.id !== snapshot.cycle?.id;
        if (changedCycle) this.command = null;
        const utc = Date.parse(snapshot.serverNow);
        this.update({ snapshot, clock: Number.isFinite(utc) ? { utc, monotonic: performance.now() } : null, loading: false, loadError: false, ...(changedCycle ? { submitted: false, error: false } : {}) });
      }
    } catch (error) {
      if (!request.signal.aborted && generation === this.generation) {
        this.update({ loading: false, loadError: !(error instanceof ApiError && error.status === 404), snapshot: error instanceof ApiError && error.status === 404 ? { enabled: false, serverNow: '', portrait: '', version: 0, messages: [] } : this.state.snapshot });
      }
    } finally {
      this.requests.delete(request);
      if (this.active && generation === this.generation) this.timer = setTimeout(() => { void this.refresh(); }, this.state.snapshot?.cycle ? 4000 : 15000);
    }
  };
  submit = async (text: string) => {
    const cycle = this.state.snapshot?.cycle;
    if (!this.active || !cycle?.canSubmit || this.state.sending || this.state.submitted || !Number.isFinite(Date.parse(cycle.deadline)) || biographyRemaining(cycle.deadline, this.state.clock, performance.now()) === 0 || !text.trim() || Array.from(text).length > 2000) return;
    if (!this.command || this.command.cycleId !== cycle.id || this.command.text !== text) this.command = { cycleId: cycle.id, commandId: this.newId(), text };
    const request = new AbortController(), generation = this.generation; this.requests.add(request); this.update({ sending: true, error: false });
    try {
      await this.api.submit(this.command, request.signal);
      if (!request.signal.aborted && generation === this.generation && this.state.snapshot?.cycle?.id === cycle.id) this.update({ submitted: true });
    } catch (error) {
      if (!request.signal.aborted && generation === this.generation && this.state.snapshot?.cycle?.id === cycle.id) {
        if (error instanceof ApiError && error.body.includes('window_closed')) this.update({ snapshot: { ...this.state.snapshot, cycle: { ...cycle, canSubmit: false } }, error: false });
        else this.update({ error: true });
      }
    } finally {
      this.requests.delete(request);
      if (!request.signal.aborted && generation === this.generation) this.update({ sending: false });
    }
  };
}
